import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Wrench, Plus, QrCode, Search, Filter, Tag, Upload, LayoutGrid, List, Download } from 'lucide-react';
import { motion } from 'framer-motion';
import { getApiUrl } from '@/config';
import type { Tool, ToolCategory, ToolStatus } from '@shared/types/inventory';
import type { Technician } from '@shared/types';
import { ToolCard } from '../components/ToolCard';
import { ToolAssignmentModal } from '../components/ToolAssignmentModal';
import { ToolModal } from '../components/ToolModal';
import { CategoryManagerModal } from '../components/CategoryManagerModal';
import { useAuth } from '@features/auth/context/AuthContext';
import { useLayout } from '@shared/contexts/LayoutContext';

interface InventoryPageProps {
    technicians?: Technician[];
    tools?: Tool[];
    onUpdateTools?: (tools: Tool[]) => void;
}

export const InventoryPage: React.FC<InventoryPageProps> = ({
    technicians = [],
    tools: propTools,
    onUpdateTools
}) => {
    const navigate = useNavigate();
    const { user } = useAuth();
    const { layoutMode } = useLayout();
    
    const canWrite = user?.role === 'admin' || 
                    (user?.permissions?.tooling === 'write') || 
                    (user?.role === 'planner' && !user?.permissions);

    // State
    // If props are provided, use them. Otherwise fallback to local state (for isolated testing/demo)
    const [localTools, setLocalTools] = useState<Tool[]>([]);

    const tools = propTools || localTools;
    const setTools = (newTools: Tool[] | ((prev: Tool[]) => Tool[])) => {
        if (onUpdateTools && propTools) {
            // We need to resolve the new state if it's a function
            // This is a bit tricky because onUpdateTools expects Tool[], not a setter function
            // But we can just handle it here if we assume we have the current state

            // Wait, if it's a function, we need to pass current TOOLS to it
            const resolved = typeof newTools === 'function' ? newTools(tools) : newTools;
            onUpdateTools(resolved);
        } else {
            setLocalTools(newTools);
        }
    };

    const [categories, setCategories] = useState<ToolCategory[]>(['Electroportatif', 'Mesure', 'Echelles', 'Vehicule', 'Informatique']);

    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState<ToolStatus | 'all'>('all');
    const [categoryFilter, setCategoryFilter] = useState<ToolCategory | 'all'>('all');

    // Modal State
    const [assignmentModalOpen, setAssignmentModalOpen] = useState(false);
    const [toolModalOpen, setToolModalOpen] = useState(false);
    const [categoryModalOpen, setCategoryModalOpen] = useState(false);

    const [selectedTool, setSelectedTool] = useState<Tool | null>(null);

    const [technicianFilter, setTechnicianFilter] = useState<string>('all');
    const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');

    // Derived State
    const filteredTools = useMemo(() => {
        return tools.filter(tool => {
            const matchesSearch = tool.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                ((tool.serialNumber || '').toLowerCase().includes(searchTerm.toLowerCase()));
            const matchesStatus = statusFilter === 'all' || tool.status === statusFilter;
            const matchesCategory = categoryFilter === 'all' || (tool.category || '').trim().toLowerCase() === (categoryFilter || '').trim().toLowerCase();
            const matchesTechnician = technicianFilter === 'all' || tool.assignedTo === technicianFilter;

            return matchesSearch && matchesStatus && matchesCategory && matchesTechnician;
        });
    }, [tools, searchTerm, statusFilter, categoryFilter, technicianFilter]);

    const stats = useMemo(() => {
        return {
            total: tools.length,
            assigned: tools.filter(t => t.status === 'assigned').length,
            maintenance: tools.filter(t => t.status === 'maintenance').length,
            lowStock: 2
        };
    }, [tools]);

    // Handlers
    const handleOpenAssignModal = (tool: Tool) => {
        setSelectedTool(tool);
        setAssignmentModalOpen(true);
    };

    const handleAssign = async (toolId: string, technicianId: string) => {
        try {
            const technician = technicians.find(t => t.id === technicianId);
            const token = localStorage.getItem('token');
            const res = await fetch(getApiUrl(`/api/tools/${toolId}/assign`), {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({
                    technicianId,
                    technicianName: technician?.name || 'Inconnu'
                })
            });
            if (res.ok) {
                const data = await res.json();
                setTools(prev => prev.map(t => t.id === toolId ? data.tool : t));
                setAssignmentModalOpen(false);
                setSelectedTool(null);
            } else {
                alert("Erreur lors de l'assignation");
            }
        } catch (e) {
            console.error(e);
            alert("Erreur réseau");
        }
    };

    const handleUnassign = async (toolId: string) => {
        try {
            const token = localStorage.getItem('token');
            const res = await fetch(getApiUrl(`/api/tools/${toolId}/return`), {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (res.ok) {
                const data = await res.json();
                setTools(prev => prev.map(t => t.id === toolId ? data.tool : t));
                setAssignmentModalOpen(false);
                setSelectedTool(null);
            } else {
                alert("Erreur lors du retour");
            }
        } catch (e) {
            console.error(e);
            alert("Erreur réseau");
        }
    };

    const handleOpenEditModal = (tool: Tool) => {
        setSelectedTool(tool);
        setToolModalOpen(true);
    };

    const handleOpenAddModal = () => {
        setSelectedTool(null);
        setToolModalOpen(true);
    };

    const handleSaveTool = (tool: Tool) => {
        setTools(prev => {
            const exists = prev.find(t => t.id === tool.id);
            if (exists) {
                return prev.map(t => t.id === tool.id ? tool : t);
            } else {
                return [...prev, tool];
            }
        });
        setToolModalOpen(false);
        setSelectedTool(null);
    };

    const handleDeleteTool = (toolId: string) => {
        if (window.confirm('Êtes-vous sûr de vouloir supprimer cet outil ?')) {
            setTools(prev => prev.filter(t => t.id !== toolId));
            setToolModalOpen(false);
            setSelectedTool(null);
        }
    };

    const handleUpdateCategories = (newCategories: ToolCategory[]) => {
        setCategories(newCategories);
    };

    const handleImportCSV = async (e: React.ChangeEvent<HTMLInputElement>) => {
        if (!e.target.files || e.target.files.length === 0) return;

        const file = e.target.files[0];
        const formData = new FormData();
        formData.append('file', file);

        try {
            const token = localStorage.getItem('token');
            const res = await fetch(getApiUrl('/api/tools/import'), {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${token}` },
                body: formData,
            });
            const data = await res.json();

            if (res.ok) {
                alert(data.message || 'Import réussi !');
                // Refresh tools
                const toolsRes = await fetch(getApiUrl('/api/tools'), {
                    headers: { 'Authorization': `Bearer ${token}` }
                });
                const newTools = await toolsRes.json();
                if (onUpdateTools && Array.isArray(newTools)) {
                    onUpdateTools(newTools);
                }
            } else {
                alert(`Erreur d'import: ${data.error}`);
            }
        } catch (err) {
            console.error(err);
            alert("Erreur lors de l'envoi du fichier.");
        }

        // Reset input
        e.target.value = '';
    };

    const handleExportCSV = () => {
        const headers = ['Nom', 'S/N', 'Catégorie', 'Statut', 'Assigné à', 'Description', 'N° Inventaire'];
        const csvRows = tools.map(tool => {
            const tech = technicians.find(t => t.id === tool.assignedTo);
            return [
                tool.name,
                tool.serialNumber || 'N/A',
                tool.category,
                tool.status,
                tech ? tech.name : 'Libre',
                tool.description || '',
                tool.inventoryNumber || ''
            ].map(val => `"${String(val).replace(/"/g, '""')}"`).join(',');
        });

        const csvContent = [headers.join(','), ...csvRows].join('\n');
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.setAttribute('href', url);
        const dateStr = new Date().toISOString().split('T')[0];
        link.setAttribute('download', `export_outillage_${dateStr}.csv`);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    // Helper to get technician object by ID (including mock ID fallback)
    const getTechnicianForTool = (techId?: string): Technician | undefined => {
        if (!techId) return undefined;
        // Search in props first
        const tech = technicians.find(t => t.id === techId);
        if (tech) return tech;
        return undefined;
    };


    return (
        <div className="min-h-screen p-6 md:p-8 max-w-7xl mx-auto space-y-8 pb-24">
            {/* Header */}
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div className="flex items-center gap-4">
                    {layoutMode !== 'sidebar' && (
                        <button
                            onClick={() => navigate('/')}
                            className="p-2 hover:bg-glass-border/10 rounded-full transition-colors text-muted hover:text-main"
                        >
                            <ArrowLeft size={24} />
                        </button>
                    )}
                    <div>
                        <h1 className="text-2xl font-bold text-main flex items-center gap-3">
                            <Wrench className="text-primary" />
                            Outillage & Parc
                        </h1>
                        <p className="text-sm text-muted">Gestion du matériel et assignations</p>
                    </div>
                </div>

                <div className="flex gap-2">
                    <button className="glass-button flex items-center gap-2 text-sm">
                        <QrCode size={18} />
                        <span className="hidden sm:inline">Scan QR</span>
                    </button>

                    {canWrite && (
                        <>
                            <label className="glass-button bg-glass-border/20 hover:bg-glass-border/40 cursor-pointer flex items-center gap-2 text-sm">
                                <Upload size={18} />
                                <span className="hidden sm:inline">Importer CSV</span>
                                <input
                                    type="file"
                                    accept=".csv"
                                    className="hidden"
                                    onChange={handleImportCSV}
                                />
                            </label>

                            <button
                                onClick={handleExportCSV}
                                className="glass-button bg-blue-500/10 text-blue-400 border-blue-500/50 hover:bg-blue-500/20 flex items-center gap-2 text-sm"
                                title="Exporter en CSV"
                            >
                                <Download size={18} />
                                <span className="hidden sm:inline">Exporter CSV</span>
                            </button>

                            <button
                                onClick={() => setCategoryModalOpen(true)}
                                className="glass-button bg-secondary/10 text-secondary border-secondary/50 hover:bg-secondary/20 flex items-center gap-2 text-sm"
                                title="Gérer les catégories"
                            >
                                <Tag size={18} />
                                <span className="hidden sm:inline">Catégories</span>
                            </button>

                            <button
                                onClick={handleOpenAddModal}
                                className="glass-button bg-primary/20 text-primary border-primary/50 hover:bg-primary/30 flex items-center gap-2 text-sm"
                            >
                                <Plus size={18} />
                                <span className="hidden sm:inline">Nouvel Outil</span>
                                <span className="sm:hidden">Ajouter</span>
                            </button>
                        </>
                    )}
                </div>
            </div>

            {/* KPI Cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {[
                    { label: 'Total Outils', value: stats.total, color: 'text-main', border: 'border-glass-border' },
                    { label: 'Sortis / Assignés', value: stats.assigned, color: 'text-blue-400', border: 'border-blue-500/30' },
                    { label: 'En Maintenance', value: stats.maintenance, color: 'text-amber-400', border: 'border-amber-500/30' },
                    { label: 'Alertes Stock', value: stats.lowStock, color: 'text-red-400', border: 'border-red-500/30' },
                ].map((stat, idx) => (
                    <div key={idx} className={`glass-panel p-4 flex flex-col items-center justify-center border ${stat.border}`}>
                        <span className={`text-2xl font-bold ${stat.color}`}>{stat.value}</span>
                        <span className="text-xs text-muted font-medium uppercase tracking-wide">{stat.label}</span>
                    </div>
                ))}
            </div>

            {/* Controls Bar */}
            <div className="glass-panel p-4 flex flex-col md:flex-row gap-4 items-center justify-between">
                {/* Search */}
                <div className="relative w-full md:w-64">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-muted" size={16} />
                    <input
                        type="text"
                        placeholder="Rechercher un outil..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="glass-input w-full !pl-9 py-2 text-sm"
                    />
                </div>

                {/* Filters */}
                <div className="flex items-center gap-2 overflow-x-auto w-full md:w-auto pb-2 md:pb-0 no-scrollbar">
                    <Filter size={16} className="text-muted mr-2 flex-shrink-0" />

                    <button
                        onClick={() => setStatusFilter('all')}
                        className={`px-3 py-1.5 rounded-lg text-xs font-medium whitespace-nowrap transition-colors border ${statusFilter === 'all' ? 'bg-white/10 text-white border-white/20' : 'bg-glass-border/10 text-muted border-transparent hover:bg-glass-border/20'}`}
                    >
                        Tout
                    </button>
                    <button
                        onClick={() => setStatusFilter('available')}
                        className={`px-3 py-1.5 rounded-lg text-xs font-medium whitespace-nowrap transition-colors border ${statusFilter === 'available' ? 'bg-emerald-500/20 text-emerald-500 border-emerald-500/50' : 'bg-glass-border/10 text-muted border-transparent hover:bg-glass-border/20'}`}
                    >
                        Dispo
                    </button>
                    <button
                        onClick={() => setStatusFilter('assigned')}
                        className={`px-3 py-1.5 rounded-lg text-xs font-medium whitespace-nowrap transition-colors border ${statusFilter === 'assigned' ? 'bg-blue-500/20 text-blue-500 border-blue-500/50' : 'bg-glass-border/10 text-muted border-transparent hover:bg-glass-border/20'}`}
                    >
                        Sortis
                    </button>
                    <button
                        onClick={() => setStatusFilter('maintenance')}
                        className={`px-3 py-1.5 rounded-lg text-xs font-medium whitespace-nowrap transition-colors border ${statusFilter === 'maintenance' ? 'bg-amber-500/20 text-amber-500 border-amber-500/50' : 'bg-glass-border/10 text-muted border-transparent hover:bg-glass-border/20'}`}
                    >
                        Maint.
                    </button>
                    <button
                        onClick={() => setStatusFilter('lost')}
                        className={`px-3 py-1.5 rounded-lg text-xs font-medium whitespace-nowrap transition-colors border ${statusFilter === 'lost' ? 'bg-red-500/20 text-red-500 border-red-500/50' : 'bg-glass-border/10 text-muted border-transparent hover:bg-glass-border/20'}`}
                    >
                        Perdu
                    </button>

                    <div className="w-px h-6 bg-glass-border mx-2"></div>

                    <select
                        value={categoryFilter}
                        onChange={(e) => setCategoryFilter(e.target.value as any)}
                        className="glass-input py-1.5 pl-2 pr-8 text-xs min-w-[120px]"
                    >
                        <option value="all">Toutes catégories</option>
                        {categories.map(cat => <option key={cat} value={cat}>{cat}</option>)}
                    </select>

                    <select
                        value={technicianFilter}
                        onChange={(e) => setTechnicianFilter(e.target.value)}
                        className="glass-input py-1.5 pl-2 pr-8 text-xs min-w-[120px]"
                    >
                        <option value="all">Tous techniciens</option>
                        {technicians.map(tech => (
                            <option key={tech.id} value={tech.id}>{tech.name}</option>
                        ))}
                    </select>

                    <div className="w-px h-6 bg-glass-border mx-2"></div>

                    {/* View Mode Toggle */}
                    <div className="flex bg-glass-border/10 p-1 rounded-lg border border-glass-border/50">
                        <button
                            onClick={() => setViewMode('grid')}
                            className={`p-1.5 rounded-md transition-all ${viewMode === 'grid' ? 'bg-white/10 text-white shadow-sm' : 'text-muted hover:text-gray-300'}`}
                            title="Vue Grille"
                        >
                            <LayoutGrid size={16} />
                        </button>
                        <button
                            onClick={() => setViewMode('list')}
                            className={`p-1.5 rounded-md transition-all ${viewMode === 'list' ? 'bg-white/10 text-white shadow-sm' : 'text-muted hover:text-gray-300'}`}
                            title="Vue Liste"
                        >
                            <List size={16} />
                        </button>
                    </div>
                </div>
            </div>

            {/* Content Area */}
            {viewMode === 'grid' ? (
                /* Grid View */
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 xl:grid-cols-5 gap-4">
                    {filteredTools.map((tool, idx) => (
                        <motion.div
                            key={tool.id}
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: idx * 0.05 }}
                        >
                            <ToolCard
                                tool={tool}
                                technician={getTechnicianForTool(tool.assignedTo)}
                                onAssign={() => handleOpenAssignModal(tool)}
                                onEdit={handleOpenEditModal}
                            />
                        </motion.div>
                    ))}

                    {filteredTools.length === 0 && (
                        <div className="col-span-full py-12 text-center text-muted">
                            <Wrench size={48} className="mx-auto mb-4 opacity-20" />
                            <p>Aucun outil ne correspond à votre recherche.</p>
                        </div>
                    )}
                </div>
            ) : (
                /* List View */
                <div className="bg-[#1e293b]/50 backdrop-blur-sm border border-white/10 rounded-xl overflow-hidden">
                    <div className="overflow-x-auto">
                        <table className="w-full text-left text-sm">
                            <thead className="bg-white/5 text-gray-400 font-medium border-b border-white/10">
                                <tr>
                                    <th className="px-6 py-4">Nom</th>
                                    <th className="px-6 py-4">Détails</th>
                                    <th className="px-6 py-4">Catégorie</th>
                                    <th className="px-6 py-4">Statut</th>
                                    <th className="px-6 py-4 text-right">Actions</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-white/10">
                                {filteredTools.map((tool) => {
                                    const tech = getTechnicianForTool(tool.assignedTo);
                                    const statusColor = {
                                        available: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30',
                                        assigned: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
                                        maintenance: 'bg-amber-500/20 text-amber-400 border-amber-500/30',
                                        lost: 'bg-red-500/20 text-red-400 border-red-500/30',
                                    }[tool.status];

                                    const statusLabel = {
                                        available: 'Disponible',
                                        assigned: tech ? `Chez ${tech.name}` : 'Sorti',
                                        maintenance: 'En Maintenance',
                                        lost: 'Perdu / Volé'
                                    }[tool.status];

                                    return (
                                        <tr key={tool.id} className="hover:bg-white/5 transition-colors group">
                                            <td className="px-6 py-4">
                                                <div className="flex items-center gap-3">
                                                    {tool.image ? (
                                                        <img src={tool.image} alt="" className="w-10 h-10 rounded object-cover bg-white/5" />
                                                    ) : (
                                                        <div className="w-10 h-10 rounded bg-white/5 flex items-center justify-center text-muted">
                                                            <Wrench size={18} />
                                                        </div>
                                                    )}
                                                    <div>
                                                        <div className="font-medium text-gray-200">{tool.name}</div>
                                                        <div className="text-xs text-muted">S/N: {tool.serialNumber || 'N/A'}</div>
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 text-gray-300 max-w-xs truncate" title={tool.description}>
                                                {tool.description || '-'}
                                            </td>
                                            <td className="px-6 py-4">
                                                <span className="px-2 py-1 rounded text-xs bg-white/5 text-gray-400 border border-white/10">
                                                    {tool.category}
                                                </span>
                                            </td>
                                            <td className="px-6 py-4">
                                                <span className={`px-2 py-1 rounded-full text-xs border ${statusColor}`}>
                                                    {statusLabel}
                                                </span>
                                            </td>
                                            <td className="px-6 py-4 text-right">
                                                {canWrite && (
                                                    <div className="flex items-center justify-end gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                                                        <button
                                                            onClick={() => handleOpenAssignModal(tool)}
                                                            className="px-3 py-1.5 rounded bg-primary/20 hover:bg-primary/30 text-primary text-xs font-medium border border-primary/30 transition-colors"
                                                        >
                                                            {tool.status === 'assigned' ? 'Gérer' : 'Assigner'}
                                                        </button>
                                                        <button
                                                            onClick={() => handleOpenEditModal(tool)}
                                                            className="px-3 py-1.5 rounded bg-white/5 hover:bg-white/10 text-gray-300 text-xs font-medium border border-white/10 transition-colors"
                                                        >
                                                            Modifier
                                                        </button>
                                                    </div>
                                                )}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                        {filteredTools.length === 0 && (
                            <div className="py-12 text-center text-muted">
                                <p>Aucun outil trouvé.</p>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* Assignment Modal */}
            <ToolAssignmentModal
                isOpen={assignmentModalOpen}
                onClose={() => setAssignmentModalOpen(false)}
                tool={selectedTool}
                technicians={technicians}
                onAssign={handleAssign}
                onUnassign={handleUnassign}
            />

            {/* Tool Add/Edit Modal */}
            <ToolModal
                isOpen={toolModalOpen}
                onClose={() => setToolModalOpen(false)}
                onSave={handleSaveTool}
                onDelete={handleDeleteTool}
                initialTool={selectedTool}
                categories={categories}
            />

            {/* Category Manager Modal */}
            <CategoryManagerModal
                isOpen={categoryModalOpen}
                onClose={() => setCategoryModalOpen(false)}
                categories={categories}
                onUpdateCategories={handleUpdateCategories}
            />
        </div>
    );
};
